import { fromJS } from "immutable"
import { createSelector, createStructuredSelector } from "reselect"
import { BrandColors, ORGANIZATION_FORM_KEY } from "shared/imports/sharedConstants"
import {
    selectSpecificCustomDescription,
    makeSelectFieldFromCustomDescription,
} from "shared/state_management/selectors"
import {
    createProfileInstanceSelector,
    makeImmutableSliceSelector,
    createDataSections,
    createSectionObject,
    createCustomFieldObjects,
    createToJsSelector,
    createSocialUserObjects,
    makeSourceUrlObjs,
    createContactCardDetailItems,
    createExtraFieldSelector,
    createSearchifyForSingleObject,
    createTransactionSummaryRowData,
    createTransactionSummaryColumnMapping,
    formatNumber,
    parseDate,
} from "shared/imports/sharedHelperFunctions"
import { selectSocialUsersFromModel } from "shared/social/social-user-selectors"
import * as customFieldSelectors from "shared/customFields/selectors"
import { selectTeamNamesFromUserdataSlice } from "shared/userdata/userdata-selectors"
import { checkRegionsWithinRegions } from "shared/imports/permissionFunctions"
import { internationalRegions } from "shared/imports/regionConstants"
import { generateSocialUserDetails, generateSocialUserUrl } from "shared/profiles/socialUser/social-user-selectors"
import { readFromCache, selectCache } from "shared/api-caching/selectors"
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"
import { selectLedgerSettingsListIds } from "shared/pac/selectors"

export const organizationSelector = (state) => state.organizationProfile

export const selectProfileInstance = createProfileInstanceSelector({
    selector: organizationSelector,
    key: "organizations",
})

export const makeOrganizationProfileSelect = makeImmutableSliceSelector(selectProfileInstance)

export const selectOrganization = makeOrganizationProfileSelect("organization", fromJS({}))
export const selectIsLoadingOrganization = makeOrganizationProfileSelect("isLoadingOrganization")

export const selectOrganizationProp = makeImmutableSliceSelector(selectOrganization)

export const selectName = selectOrganizationProp("name")

export const selectEnrollmentSize = selectOrganizationProp("enrollment")

export const selectId = (state, props) => (props && props.profileId) || (props.params && props.params.profileId)
export const selectResourceUri = selectOrganizationProp("resource_uri")
export const selectOrganizationOrganization = selectOrganizationProp("organization")
export const selectAddress = selectOrganizationProp("shipping_address")
export const selectAbbrev = selectOrganizationProp("abbrev")
export const selectPhoneNumber = selectOrganizationProp("formatted_phone")
export const selectCounty = selectOrganizationProp("county")
export const selectOrganizationType = selectOrganizationProp("organization_type")
export const selectOrganizationTypeString = createSelector(
    selectOrganizationType,
    (organizationType) =>
        organizationType && DjangIO.app.person.types.PublicOrganizationType.by_value(organizationType).label,
)

export const selectHasStaffers = selectOrganizationProp("has_staffers")

export const selectIsCustomOrg = createSelector(
    selectOrganizationOrganization,
    selectOrganizationType,
    (org, type) =>
        org &&
        DjangIO.app.userdata.models.Organization.idFromResourceUri(org) === Userdata.organization_id &&
        type === DjangIO.app.person.types.PublicOrganizationType.custom.value,
)

export const selectMostRecentRegion = selectOrganizationProp("region")
export const selectIsInternationalOrganization = createSelector(
    selectMostRecentRegion,
    (region) => Boolean(region) && checkRegionsWithinRegions([region], internationalRegions),
)
export const selectMajorOrganizationType = selectOrganizationProp("_ph_major_organization_type")
export const selectMajorOrganizationTypeStrings = createSelector(
    selectMajorOrganizationType,
    (type) => type && DjangIO.app.person.newtypes.NewMajorOrganizationType.by_value(type).label,
)

// profiles new
export const selectCustomDataDescription = selectSpecificCustomDescription(
    DjangIO.app.models.QuorumDataType.public_organization.key,
)

export const makeOrganizationDescriptionSelect = makeSelectFieldFromCustomDescription(selectCustomDataDescription)

export const selectCustomDescriptionText = makeOrganizationDescriptionSelect("text")
export const selectCustomDescriptionId = makeOrganizationDescriptionSelect("id")

// Custom Orgs save their tag_dict straight on the PubOrg model itself. Other PubOrgs that are shared between clients (Outlets/Government), save them to the DataDescription model
export const selectTagDictNotJS = createSelector(
    makeOrganizationDescriptionSelect("tag_dict"),
    (dataDescriptionTagDict) => dataDescriptionTagDict,
)
export const selectTagDict = createToJsSelector(selectTagDictNotJS, {})

export const selectCurrentTab = makeOrganizationDescriptionSelect("currentTab")

export const selectImgUrl = selectOrganizationProp("image_url")
export const selectParentOrganizationName = selectOrganizationProp("parent_organization_name")
export const selectParentOrganizationId = createSelector(
    selectOrganizationProp("parent_organization"),
    (parentOrgUrl) => DjangIO.app.person.models.PublicOrganization.idFromResourceUri(parentOrgUrl),
)

export const selectIsOutlet = createSelector(
    selectOrganizationProp("_ph_sector_type"),
    (sectorType) => sectorType === DjangIO.app.person.newtypes.SectorType.media.value,
)

export const selectWebsite = selectOrganizationProp("website")
export const selectEmail = selectOrganizationProp("email")
export const isLocal = createSelector(
    selectOrganizationProp("region"),
    (region) => region && DjangIO.app.models.Region.by_value(region).is_local_region,
)
export const selectWikiPage = selectOrganizationProp("wikipedia_url")
export const selectSourceUrls = selectOrganizationProp("source_urls")
export const selectPacOrganizationType = selectOrganizationProp("pac_org_type")
export const selectPacOrganizationTypeString = createSelector(
    selectPacOrganizationType,
    (pacOrganizationType) =>
        pacOrganizationType && DjangIO.app.pac.types.EntityType.by_value(pacOrganizationType).label,
)

export const selectLimitedToTeams = selectOrganizationProp("limited_to_teams")
export const selectTeamsForProfile = selectTeamNamesFromUserdataSlice(selectLimitedToTeams)

export const selectImmutableTransactionSummary = makeOrganizationProfileSelect("transactionSummary")
export const selectTransactionSummary = createToJsSelector(selectImmutableTransactionSummary)
export const selectIsLoadingTransactionSummary = makeOrganizationProfileSelect("isLoadingTransactionSummary")
export const selectFetchTransactionSummaryFailed = makeOrganizationProfileSelect("fetchTransactionSummaryFailed")

// Load the summary if it is not loading, has not failed, and if the data does not exist
export const selectShouldLoadTransactionSummary = createSelector(
    selectIsLoadingTransactionSummary,
    selectFetchTransactionSummaryFailed,
    selectTransactionSummary,
    selectLedgerSettingsListIds,
    selectId,
    (isLoading, fetchFailed, summary, ledgerSettingsListIds, id) =>
        !isLoading &&
        !fetchFailed &&
        !summary &&
        ledgerSettingsListIds &&
        Boolean(ledgerSettingsListIds[0]) &&
        Boolean(id),
)

export const selectTransactionSummaryColumnMapping = createSelector(selectTransactionSummary, (transactionSummary) =>
    createTransactionSummaryColumnMapping({
        transactionSummary,
        qdt: DjangIO.app.models.QuorumDataType.public_organization.value,
    }),
)

export const selectTransactionSummaryRowData = createSelector(selectTransactionSummary, (transactionSummary) => {
    const rowData = createTransactionSummaryRowData(transactionSummary, "year")
    // Reverse rowData for the organization table to show recent years first [2020, 2019 ...]
    return rowData && rowData.reverse()
})

export const selectTableSection = ({ sectionComponent }) =>
    createSelector(
        selectIsCustomOrg,
        selectId,
        selectTransactionSummaryRowData,
        selectTransactionSummaryColumnMapping,
        (isCustomOrg, id, rowData, columnMapping) =>
            isCustomOrg &&
            createSectionObject({
                title: "Transaction Summary",
                data: [{ id, rowData, columnMapping }],
                sectionComponent,
            }),
    )

export const selectMetaOrganizationType = createSelector(
    selectOrganizationProp("_ph_meta_organization_type"),
    (orgType) =>
        DjangIO.app.person.newtypes.MetaOrganizationType.by_value(orgType)
            ? DjangIO.app.person.newtypes.MetaOrganizationType.by_value(orgType).label
            : "N/A",
)

export const selectOutletCoverage = createSelector(
    selectOrganizationProp("ln_category"),
    selectOrganizationProp("intrado_coverage"),
    selectOrganizationProp("intrado_media_type"),
    selectOrganizationProp("intrado_audience_focus"),
    (lnCategory, intradoCoverage, intradoMediaType, intradoAudienceFocus) => {
        const msc_misc_lexis_nexis = [
            DjangIO.app.media_monitoring.types.MediaSourceCategory.misc.label,
            DjangIO.app.media_monitoring.types.MediaSourceCategory.general.label,
        ]
        const msc_consumer_intrado = [DjangIO.app.media_monitoring.types.IntradoAudienceFocus.consumer.label]
        const msc_journal_intrado = [DjangIO.app.media_monitoring.types.IntradoMediaType.journal.label]
        const msc_local_intrado = [
            DjangIO.app.media_monitoring.types.IntradoCoverage.local.label,
            DjangIO.app.media_monitoring.types.IntradoCoverage.regional.label,
        ]
        const msc_misc_intrado = [DjangIO.app.media_monitoring.types.IntradoCoverage.other.label]
        const msc_national_intrado = [
            DjangIO.app.media_monitoring.types.IntradoCoverage.national.label,
            DjangIO.app.media_monitoring.types.IntradoCoverage.international.label,
        ]
        const msc_press_wire_intrado = [DjangIO.app.media_monitoring.types.IntradoMediaType.news_wire.label]
        const msc_trade_intrado = [DjangIO.app.media_monitoring.types.IntradoAudienceFocus.trade.label]
        const combined_labels = []

        if (lnCategory) {
            const lnCategoryLabel = DjangIO.app.media_monitoring.types.MediaSourceCategory.by_value(lnCategory).label
            if (msc_misc_lexis_nexis.includes(lnCategoryLabel)) {
                combined_labels.push(DjangIO.app.media_monitoring.types.MediaSourceCategory.misc.label)
            } else {
                combined_labels.push(DjangIO.app.media_monitoring.types.MediaSourceCategory.by_value(lnCategory).label)
            }
        }

        if (intradoAudienceFocus) {
            const intradoAudienceFocusLabel =
                DjangIO.app.media_monitoring.types.IntradoAudienceFocus.by_value(intradoAudienceFocus).label
            if (msc_consumer_intrado.includes(intradoAudienceFocusLabel)) {
                combined_labels.push(DjangIO.app.media_monitoring.types.MediaSourceCategory.consumer.label)
            }
            if (msc_trade_intrado.includes(intradoAudienceFocusLabel)) {
                combined_labels.push(DjangIO.app.media_monitoring.types.MediaSourceCategory.trade.label)
            }
        }
        if (intradoMediaType) {
            const intradoMediaTypeLabel =
                DjangIO.app.media_monitoring.types.IntradoMediaType.by_value(intradoMediaType).label
            if (msc_journal_intrado.includes(intradoMediaTypeLabel)) {
                combined_labels.push(DjangIO.app.media_monitoring.types.MediaSourceCategory.journal.label)
            }
            if (msc_press_wire_intrado.includes(intradoMediaTypeLabel)) {
                combined_labels.push(DjangIO.app.media_monitoring.types.MediaSourceCategory.press_wire.label)
            }
        }
        if (intradoCoverage) {
            const intradoCoverageLabel =
                DjangIO.app.media_monitoring.types.IntradoCoverage.by_value(intradoCoverage).label
            if (msc_local_intrado.includes(intradoCoverageLabel)) {
                combined_labels.push(DjangIO.app.media_monitoring.types.MediaSourceCategory.local.label)
            }
            if (msc_misc_intrado.includes(intradoCoverageLabel)) {
                combined_labels.push(DjangIO.app.media_monitoring.types.MediaSourceCategory.misc.label)
            }
            if (msc_national_intrado.includes(intradoCoverageLabel)) {
                combined_labels.push(DjangIO.app.media_monitoring.types.MediaSourceCategory.national.label)
            }
        }
        const deduped_lables = new Set(combined_labels)
        const outlet_coverage_labels = Array.from(deduped_lables).sort().join(", ")
        return outlet_coverage_labels || "N/A"
    },
)

export const selectOrganizationInfoSections = ({ selectorFunctions, sectionComponent, dataItems }) =>
    createSelector(
        [
            selectName,
            selectAddress,
            selectAbbrev,
            selectPhoneNumber,
            selectSocialUsersFromModel(DjangIO.app.person.models.PublicOrganization.__name__, selectOrganization),
            selectParentOrganizationName,
            selectWebsite,
            selectEmail,
            selectWikiPage,
            selectSourceUrls,
            selectTeamsForProfile,
            selectOrganizationTypeString,
            selectMajorOrganizationTypeStrings,
            selectIsInternationalOrganization,
            selectPacOrganizationTypeString,
            selectIsOutlet,
            selectMetaOrganizationType,
            selectOutletCoverage,
            selectEnrollmentSize,
        ],
        (
            name,
            address,
            abbrev,
            phoneNumber,
            socialUsers,
            parentOrgName,
            website,
            email,
            wikiPage,
            sourceUrls,
            teamsForProfile,
            organizationType,
            majorOrganizationTypes,
            internationalOrg,
            pacOrganizationType,
            isOutlet,
            metaOrganizationType,
            outletCoverage,
            enrollmentSize,
        ) => {
            const sectionData = [
                {
                    title: "Name",
                    value: name,
                    width: 6,
                },
                {
                    title: "Parent Organization",
                    value: parentOrgName,
                    width: 6,
                },
                {
                    title: "Organization Type",
                    value: organizationType,
                },
                internationalOrg && {
                    title: "Organization Types",
                    value: majorOrganizationTypes,
                },
                !isOutlet && {
                    title: "Address",
                    value: address,
                },
                {
                    title: "Abbreviation",
                    value: abbrev,
                },
                !isOutlet && {
                    title: "Phone Number",
                    value: phoneNumber,
                },
                !isOutlet && {
                    title: "Email",
                    value: email,
                },
                {
                    title: "Website",
                    value: website,
                    isLink: true,
                },
                {
                    title: "Wikipedia Page",
                    value: wikiPage,
                    onPress: selectorFunctions && selectorFunctions({ wikiPage }).wikiPage,
                    icon: "globe",
                    isLink: true,
                    width: 3,
                },
                isOutlet && {
                    title: "Outlet Type",
                    value: metaOrganizationType,
                },
                isOutlet && {
                    title: "Outlet Coverage",
                    value: outletCoverage,
                },
                {
                    title: "Limited to Teams",
                    value: teamsForProfile,
                },
                ...(isFeatureEnabled("ff_pac")
                    ? [
                          {
                              title: "PAC Organization Type",
                              value: pacOrganizationType,
                          },
                      ]
                    : []),
                ...createSocialUserObjects({
                    socialUsers: socialUsers.toJS ? socialUsers.toJS() : socialUsers,
                    selectorFunctions,
                    dataItems,
                }),
                ...(isFeatureEnabled("ff_school_board_orgs_and_officials")
                    ? [
                          {
                              title: "Enrollment Size",
                              value: enrollmentSize,
                          },
                      ]
                    : []),
                ...makeSourceUrlObjs(sourceUrls),
            ]
            const sectionObj = createSectionObject({
                title: "Information",
                data: createDataSections(sectionData),
                id: "organizationInfoSection",
                sectionComponent,
            })
            return sectionObj
        },
    )

export const selectIsLoading = createSelector(
    selectIsLoadingOrganization,
    customFieldSelectors.selectIsLoading,
    (isLoadingOrganization, isLoadingCustomTags) => isLoadingOrganization || isLoadingCustomTags,
)

export const makeCustomDescriptionSelect = (key) =>
    createSelector(selectCustomDataDescription, (customDescription) =>
        customDescription && customDescription.get(key) ? customDescription.get(key) : undefined,
    )

export const selectDLCGeneralInfoSection = ({ sectionComponent }) =>
    createSelector(selectName, selectOrganizationTypeString, (name, orgType) => {
        const generalInfo = [
            {
                title: "Name",
                value: name,
            },
            {
                title: "Organization Type",
                value: orgType,
            },
        ]

        const generalInfoObj = createSectionObject({
            title: "General",
            data: createDataSections(generalInfo),
            id: "organizationGeneralInfo",
            sectionComponent,
            canEditSection: true,
        })
        return generalInfoObj
    })

export const selectCustomDataFields = ({ sectionComponent }) =>
    createSelector(
        selectTagDict,
        customFieldSelectors.selectCustomFieldNameDictForProfile(
            DjangIO.app.models.QuorumDataType.public_organization.value,
        ),
        (tagDict, customFields) => {
            const customData = [
                ...createCustomFieldObjects({ customFields, tagDict }).map((dataObj) => Object.assign(dataObj)),
            ]

            const customDataObj = createSectionObject({
                title: "Custom Fields",
                data: createDataSections(customData),
                id: "organizationCustomData",
                sectionComponent,
                canEditSection: true,
            })
            return customDataObj
        },
    )

export const isAgencyOrg = createSelector(selectOrganizationType, (type) => {
    const { PublicOrganizationType } = DjangIO.app.person.types
    // this is from the PublicOrganizationType.executive_organization list
    const agencyTypes = [
        PublicOrganizationType.federal_executive.value,
        PublicOrganizationType.federal_executive_office.value,
        PublicOrganizationType.federal_executive_agency.value,
        PublicOrganizationType.federal_executive_department.value,
        PublicOrganizationType.state_executive.value,
        PublicOrganizationType.state_executive_office.value,
        PublicOrganizationType.state_executive_agency.value,
        PublicOrganizationType.state_executive_department.value,
    ]
    return agencyTypes.includes(type)
})

/**
 * A `NominationOrg` is a `PublicOrganization` to which the US President can nominate a person.
 * It can be any of the four federal executive organizations or the two judicial organizations.
 */
export const isNominationOrg = createSelector(selectOrganizationType, (type) => {
    const { PublicOrganizationType } = DjangIO.app.person.types
    // include all federal executive and judicial orgs
    const agencyTypes = [
        // Including all federal executive orgs
        PublicOrganizationType.federal_executive.value,
        PublicOrganizationType.federal_executive_office.value,
        PublicOrganizationType.federal_executive_agency.value,
        PublicOrganizationType.federal_executive_department.value,
        // Including all judicial orgs
        PublicOrganizationType.federal_judiciary.value,
        PublicOrganizationType.state_judiciary.value,
    ]
    return agencyTypes.includes(type)
})

// Outlet specific Selectors
export const selectImmutableBeats = createExtraFieldSelector({
    key: "beats",
    objectSelector: selectOrganizationProp,
})
export const selectBeats = createSelector(
    selectImmutableBeats,
    (beats) => beats && beats.map((beat) => beat.get("beat__name")).join(", "),
)

export const selectFrequency = createSelector(
    selectOrganizationProp("intrado_frequency"),
    (publication_frequency) =>
        publication_frequency &&
        DjangIO.app.media_monitoring.types.IntradoFrequency.by_value(publication_frequency).label,
)

export const selectCity = selectOrganizationProp("shipping_city")
export const selectState = selectOrganizationProp("shipping_state")
export const selectCountry = selectOrganizationProp("shipping_country")
export const selectLocation = createSelector(selectCity, selectState, selectCountry, (city, state, country) =>
    [city, country === "USA" ? state : country]
        .filter((e) => Boolean(e)) // Remove empty strings
        .join(", "),
)

export const selectContactCardData = ({ sectionComponent }) =>
    createSelector(
        selectIsOutlet,
        selectOrganization,
        selectBeats,
        selectFrequency,
        selectEmail,
        selectPhoneNumber,
        selectLocation,
        selectAddress,
        (isOutlet, organization, beats, publication_frequency, email, phoneNumber, location, address) => {
            if (!isOutlet) {
                return null
            }

            const contactData = [
                {
                    circleColor: BrandColors.ULTRAVIOLET,
                    detailItems: createContactCardDetailItems({
                        email,
                        phoneNumber,
                        address,
                        location,
                    }),
                    infoItems: [
                        {
                            label: "Beats",
                            text: beats,
                            textType: "secondary",
                        },
                        {
                            label: "Frequency",
                            text: publication_frequency,
                            textType: "secondary",
                        },
                    ],
                    icon: "building",
                    imageUrl: organization.get("s3_image_url"),
                    name: organization.get("name"),
                    subtitle:
                        organization.get("_ph_major_organization_type") &&
                        DjangIO.app.person.newtypes.NewMajorOrganizationType.by_value(
                            organization.get("_ph_major_organization_type"),
                        ).label,
                },
            ]

            const contactCardObj = createSectionObject({
                title: "Contact Card",
                data: contactData,
                id: "organizationContactCard",
                sectionComponent,
            })

            return contactCardObj
        },
    )

export const selectImmutableSocialUsers = createExtraFieldSelector({
    key: "social_users",
    objectSelector: selectOrganizationProp,
    defaultVal: fromJS([]),
})

export const selectSocialMediaData = ({ sectionComponent }) =>
    createSelector(
        selectIsOutlet,
        isFeatureEnabled("ff_profile_social_icons")
            ? selectSocialUsersFromModel(DjangIO.app.person.models.PublicOrganization.__name__, selectOrganization)
            : selectImmutableSocialUsers,
        selectId,
        (isOutlet, socialUsers, profileId) => {
            if (!isOutlet || !socialUsers) {
                return null
            }

            const data = []

            socialUsers.forEach((socialUser) => {
                const socialUserType = DjangIO.app.social.models.SocialUserType.by_value(
                    socialUser.get("social_user_type"),
                )

                data.push({
                    details: generateSocialUserDetails(socialUser, socialUserType),
                    socialUserType,
                    onClick: () => window.open(generateSocialUserUrl(socialUser, socialUserType)),
                    segueToSearchProps: isFeatureEnabled("ff_comms_social_media") && {
                        quorumDataType: DjangIO.app.models.QuorumDataType.document,
                        initialFilter: {
                            document_type__in: [socialUserType.document_type],
                            ...createSearchifyForSingleObject("searchify_source_public_organization", profileId),
                        },
                    },
                })
            })

            return createSectionObject({
                title: "Social Media",
                data,
                id: "pressContactSocialMediaSection",
                sectionComponent,
            })
        },
    )

const selectCirculation = selectOrganizationProp("intrado_circulation")
const selectTags = selectOrganizationProp("intrado_tags", fromJS([]))
const selectLanguage = createSelector(selectOrganizationProp("intrado_language"), (language) =>
    DjangIO.app.document.types.Language.by_value(language)
        ? DjangIO.app.document.types.Language.by_value(language).label
        : "N/A",
)
const selectCoverage = createSelector(selectOrganizationProp("intrado_coverage"), (coverage) =>
    DjangIO.app.media_monitoring.types.IntradoCoverage.by_value(coverage)
        ? DjangIO.app.media_monitoring.types.IntradoCoverage.by_value(coverage).label
        : "N/A",
)
const selectAudienceFocus = createSelector(selectOrganizationProp("intrado_audience_focus"), (audienceFocus) =>
    DjangIO.app.media_monitoring.types.IntradoAudienceFocus.by_value(audienceFocus)
        ? DjangIO.app.media_monitoring.types.IntradoAudienceFocus.by_value(audienceFocus).label
        : "N/A",
)

const selectLastUpdated = createSelector(selectOrganizationProp("_ph_intrado_last_updated", "N/A"), (lastUpdated) =>
    parseDate(lastUpdated),
)

export const selectImmutableDma = createExtraFieldSelector({
    key: "market_areas",
    objectSelector: selectOrganizationProp,
})
export const selectDma = createSelector(
    selectImmutableDma,
    (marketAreas) => marketAreas && marketAreas.map((dma) => dma.get("designated_market_area__name")).join(", "),
)

export const selectAboutData = ({ sectionComponent }) =>
    createSelector(
        [
            selectIsOutlet,
            selectCirculation,
            selectTags,
            selectLanguage,
            selectCoverage,
            selectAudienceFocus,
            selectDma,
            selectCountry,
            selectState,
            selectLastUpdated,
        ],
        (isOutlet, circulation, tags, language, coverage, audienceFocus, dma, country, state, lastUpdated) => {
            if (!isOutlet) {
                return null
            }

            const aboutData = [
                {
                    title: "Circulation",
                    value: circulation ? formatNumber(circulation) : "N/A",
                    width: 3,
                },
                {
                    title: "Tags",
                    value: tags.join(", "),
                    width: 3,
                },
                {
                    title: "Language",
                    value: language,
                    width: 3,
                },
                {
                    title: "Coverage",
                    value: coverage,
                    width: 3,
                },
                {
                    title: "Audience Focus",
                    value: audienceFocus,
                    width: 3,
                },
                {
                    title: "DMA",
                    value: dma,
                    width: 3,
                },
                {
                    title: "Country",
                    value: country,
                    width: 3,
                },
                {
                    title: "State",
                    value: state,
                    width: 3,
                },
                {
                    title: "Last Updated",
                    value: lastUpdated,
                    width: 3,
                },
            ]

            const aboutObj = createSectionObject({
                title: "About",
                data: createDataSections(aboutData),
                id: "publicOrganizationAboutSection",
                sectionComponent,
            })

            return aboutObj
        },
    )

export const selectForm = (state) => state.form

export const selectOrganizationForm = createSelector(selectForm, (form) => form && form.get(ORGANIZATION_FORM_KEY))

export const selectFormValues = createSelector(
    selectOrganizationForm,
    (organizationForm) => organizationForm && organizationForm.get("values"),
)

export const selectInitialFormValues = createSelector(
    [selectCustomDescriptionId, selectCache, selectTagDictNotJS],
    (customDescriptionId, cache, previousTagDict) => {
        if (customDescriptionId) {
            // If DataDescription between this PubOrg and the organization exists
            const url = DjangIO.app.userdata.models.DataDescription.objects.get(customDescriptionId).buildEndpoint()

            const { tag_dict } = readFromCache(cache, url)

            return tag_dict
        } else {
            return previousTagDict
        }
    },
)

const tagOwner = DjangIO.app.models.QuorumDataType.public_organization

export const selectIsEditablePacOrg = createSelector(selectOrganizationProp("pac_org_type"), (pac_org_type) => {
    let user_selectable_pac_org_type = true
    if (pac_org_type) {
        user_selectable_pac_org_type =
            DjangIO.app.pac.types.EntityType.by_value(pac_org_type).user_selectable_pac_org_type
    }
    return user_selectable_pac_org_type
})

export const selectCanEditPublicOrg = createSelector(
    selectIsCustomOrg,
    selectIsEditablePacOrg,
    (isCustom, canEditPacOrg) => !isCustom || canEditPacOrg,
)

export const publicOrganizationCustomFieldsFormConnection = createStructuredSelector({
    organization: selectOrganization,
    profileId: selectId,
    resourceUri: selectResourceUri,
    dataDescriptionId: selectCustomDescriptionId,
    isLoading: selectIsLoading,

    customFields: customFieldSelectors.selectCustomFieldsConnection(tagOwner.value),
    customFieldNameDict: customFieldSelectors.selectCustomFieldNameDict(tagOwner.value),

    initialValues: selectInitialFormValues,
    formValues: selectFormValues,

    quorumDataType: () => tagOwner,
})
